/** Render queue: new render (quality, fps, scope), still export, progress/ETA/cancel/log, download. */

import { sortedScenes } from "@studio/engine";
import { Download, FileText, Image as ImageIcon, Loader2, X } from "lucide-react";
import { useState } from "react";
import { api, fileUrl, type Job } from "../api";
import { flush } from "./save";
import { useEditor } from "./store";
import { Row, Select } from "./ui";

export function RenderPanel() {
  const project = useEditor((s) => s.project)!;
  const jobs = useEditor((s) => s.jobs);
  const time = useEditor((s) => s.time);
  const [quality, setQuality] = useState<"draft" | "full" | "4k">("full");
  const [fps, setFps] = useState(String(project.output.fps));
  const [scope, setScope] = useState<"all" | "scene" | "range">("all");
  const [sceneId, setSceneId] = useState(project.scenes[0]?.id ?? "");
  const [range, setRange] = useState({ start: 0, end: 10 });
  const [err, setErr] = useState("");
  const [logFor, setLogFor] = useState<Job | null>(null);
  const close = () => useEditor.getState().set({ panel: null });

  const start = async () => {
    setErr("");
    try {
      await flush();
      await api.render(project.id, { quality, fps: Number(fps), scope, sceneId, start: range.start, end: range.end });
      window.dispatchEvent(new Event("studio:poll"));
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const stillNow = async () => {
    setErr("");
    try {
      await flush();
      await api.still(project.id, time, quality === "4k" ? "4k" : "full");
      window.dispatchEvent(new Event("studio:poll"));
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const showLog = async (j: Job) => setLogFor(await api.job(j.id));

  return (
    <div className="drawer">
      <header>
        <h3>Render</h3>
        <button className="icon" onClick={close}><X size={18} /></button>
      </header>
      <section>
        <Row label="Quality">
          <Select value={quality} options={[["draft", "Draft 720p (fast)"], ["full", "Full 1080p"], ["4k", "4K 2160p"]]} onChange={setQuality} />
        </Row>
        <Row label="Frame rate">
          <Select value={fps} options={[["30", "30 fps"], ["60", "60 fps"], ["25", "25 fps"]]} onChange={setFps} />
        </Row>
        <Row label="Export">
          <Select value={scope} options={[["all", "Whole project"], ["scene", "One scene"], ["range", "Time range"]]} onChange={setScope} />
        </Row>
        {scope === "scene" && (
          <Row label="Scene">
            <Select value={sceneId} options={sortedScenes(project).map((s) => [s.id, `${s.number ? s.number + " · " : ""}${s.name || s.id}`] as [string, string])} onChange={setSceneId} />
          </Row>
        )}
        {scope === "range" && (
          <Row label="From – to">
            <span className="range">
              <input className="num" type="number" step="0.1" value={range.start} onChange={(e) => setRange({ ...range, start: +e.target.value })} />
              –
              <input className="num" type="number" step="0.1" value={range.end} onChange={(e) => setRange({ ...range, end: +e.target.value })} /> s
            </span>
          </Row>
        )}
        <div className="analysis-actions">
          <button onClick={stillNow} title="Export the frame at the playhead as PNG"><ImageIcon size={14} /> Still PNG</button>
          <button className="primary" onClick={start}>Render MP4</button>
        </div>
        {err && <p className="bad">{err}</p>}
      </section>
      <section className="jobs">
        <div className="job-top">
          <h4>Queue</h4>
          <button className="link small" onClick={() => useEditor.getState().set({ panel: "renders" })} title="Label, mark final or delete finished renders">Manage renders…</button>
        </div>
        {jobs.length === 0 && <p className="muted small">No renders yet.</p>}
        {jobs.map((j) => (
          <JobRow key={j.id} job={j} pid={project.id} onLog={() => showLog(j)} />
        ))}
      </section>
      {logFor && (
        <div className="modal-back" onPointerDown={() => setLogFor(null)}>
          <div className="modal log" onPointerDown={(e) => e.stopPropagation()}>
            <header>
              <h3>Log</h3>
              <button className="icon" onClick={() => setLogFor(null)}><X size={18} /></button>
            </header>
            <pre>{logFor.log || "(empty)"}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function JobRow({ job, pid, onLog }: { job: Job; pid: string; onLog: () => void }) {
  const active = job.status === "queued" || job.status === "running" || job.status === "cancelling";
  const p = job.params as { quality?: string; fps?: number; scope?: string; t?: number };
  const title = job.kind === "still" ? `Still at ${p.t?.toFixed(2)} s` : `${p.quality} · ${p.fps} fps · ${p.scope}`;
  return (
    <div className={`job ${job.status}`}>
      <div className="job-top">
        <strong>{title}</strong>
        <span className="muted small">{new Date(job.created * 1000).toLocaleTimeString()}</span>
      </div>
      {active && (
        <>
          <div className="progress"><div style={{ width: `${Math.round(job.progress * 100)}%` }} /></div>
          <div className="job-top small">
            <span>
              <Loader2 size={12} className="spin" /> {job.status === "queued" ? "Queued" : job.message}
              {job.eta != null && job.status === "running" ? ` · ${fmtEta(job.eta)} left` : ""}
            </span>
            {job.status !== "cancelling" && <button className="link" onClick={() => api.cancel(job.id).then(() => window.dispatchEvent(new Event("studio:poll")))}>Cancel</button>}
          </div>
        </>
      )}
      {job.status === "done" && job.output?.file && job.output.deleted && <div className="small muted">Deleted</div>}
      {job.status === "done" && job.output?.file && !job.output.deleted && (
        <div className="job-top small">
          <a className="button" href={fileUrl(pid, job.output.file) + "?download=1"} download><Download size={13} /> Download</a>
          {job.output.srt && <a className="button" href={fileUrl(pid, job.output.srt) + "?download=1"} download title="Captions for this render">.srt</a>}
          {job.output.vtt && <a className="button" href={fileUrl(pid, job.output.vtt) + "?download=1"} download>.vtt</a>}
          <span className="muted">
            {fmtSize(job.output.size ?? 0)}
            {job.output.seconds ? ` · rendered in ${fmtEta(job.output.seconds)} (${job.output.fps} fps)` : ""}
          </span>
        </div>
      )}
      {(job.status === "error" || job.status === "cancelled") && <div className="small bad">{job.status === "error" ? job.message : "Cancelled"}</div>}
      <button className="link small" onClick={onLog}><FileText size={12} /> Log</button>
    </div>
  );
}

function fmtEta(s: number): string {
  if (s < 60) return `${Math.round(s)} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}
function fmtSize(b: number): string {
  return b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} kB`;
}
